import * as React from 'react'
import {
  useAngularServices,
  ButtonElement,
  CommonPageHeader,
  TextInput,
} from '@/react/components'
import { useRouter } from '@/react/hooks'
import { css } from '@emotion/react'
import { useCallback } from 'react'
import { debounce } from 'lodash'

import {
  SignatureElement,
  NotesElement,
  fetchData,
  sendForm,
  Wildcard,
  PartyObservedField,
  Category,
} from './components'
import {
  ApplicationConfig,
  GeneralSettings,
  GenericOption,
  NoteType,
  ObservationBody,
  ReportAnswer,
  ReportQuestion,
  ReportSettings,
  SignatureType,
} from '@/react/types'
import { TextInputStyle } from '@screens/components'
import moment from 'moment/moment'
import { ObservationNavBar } from '@screens/Observations/components/ObservationNavBar'
import { useEffect, useState } from 'react'

interface CategoryQuestionsResponse {
  data: {
    results: ReportQuestion[]
    count: number
  }
}

const ANSWER: ReportAnswer = {
  answer: 'n/a',
  deleted: false,
  party_observed: [],
  notes: [],
  corrective_actions: [],
  photos: [],
  severity: 1,
  answer_party_observed_answer_list: [],
  copy_sequence_number: 0,
  edited: false,
  category: 0,
  client_object_key: '',
  date_created: '',
  date_updated: '',
  question: 0,
  id: 0,
  internal_assignees: [],
  lat: 0,
  lon: 0,
  observation: 0,
  reference: '',
  user: 0,
  location_radius: 0,
  location_age: 0,
  party_observed_easy_acces_data: {},
  synchronised: [{ synchronised: false, user: 0 }],
}

export function CreateEditBase() {
  const { Api, CurrentUser, Notification } = useAngularServices()
  const { stateService } = useRouter()

  const observationID = stateService.params.observation
  const appID = stateService.params.app
  const userId = CurrentUser.getId()

  const [baseObservationData, setBaseObservationData] =
    React.useState<ObservationBody | null>(null)
  const [questionData, setQuestionData] = React.useState<ReportQuestion[]>([])
  const [settings, setSettings] = React.useState<{
    generalSettings: GeneralSettings
    reportSettings: ReportSettings
  } | null>(null)
  const [wildcard, setWildcard] = React.useState<string>('')
  const [title, setTitle] = React.useState<string>('')
  const [titleManuallyEdited, setTitleManuallyEdited] =
    React.useState<boolean>(false)
  const [wildcardId, setWildcardId] = React.useState<string | number>('')
  const [memoryState, setMemoryState] = React.useState<{
    [key: string]: ReportAnswer[]
  } | null>(null)
  const [signatures, setSignatures] = React.useState<SignatureType[]>([])
  const [notes, setNotes] = React.useState<NoteType[]>([])
  const [partyObservedOptions, setPartyObservedOptions] = React.useState<
    GenericOption[]
  >([])
  const [globalPartyObserved, setGlobalPartyObserved] = React.useState<
    GenericOption[]
  >([])
  const [disableButton, setDisableButton] = React.useState<boolean>(false)
  const [saveButtonPressed, setSaveButtonPressed] = React.useState<number>(0)
  const [page, setPage] = React.useState<number>(1)
  const [questionDataByCategory, setQuestionDataByCategory] = useState<{
    [categoryId: number]: ReportQuestion[]
  }>({})
  const setGlobalPO = (POs: GenericOption[]) => {
    if (!questionData) return
    setGlobalPartyObserved(POs)
  }

  useEffect(() => {
    if (stateService.params.pageNumber) {
      setPage(+stateService.params.pageNumber)
    }
  }, [])

  const answerTitleSet = async (
    localMemoryState?: { [key: string]: ReportAnswer[] } | null,
  ) => {
    if (!baseObservationData) return
    await sendForm(
      localMemoryState ?? memoryState,
      Api,
      signatures,
      notes,
      Number(observationID),
      stateService,
      setDisableButton,
      wildcard,
      Number(wildcardId),
      baseObservationData,
      true,
      titleManuallyEdited,
      Notification,
      Number(userId),
    )
  }

  // Create a debounced version of answerTitleSet to prevent multiple activation calls
  const debouncedAnswerTitleSet = React.useMemo(
    () =>
      debounce(
        async (localMemoryState?: { [key: string]: ReportAnswer[] } | null) => {
          await answerTitleSet(localMemoryState)
        },
        1000,
      ), // 1 second debounce to batch multiple answer changes while staying responsive
    [baseObservationData, observationID], // Only recreate if these dependencies change
  )

  React.useEffect(() => {
    const requestParams = {
      deleted: '',
      observation: Number(observationID),
      page_size: 10,
      application: Number(appID),
    }
    fetchData(
      Api,
      requestParams,
      Number(observationID),
      userId,
      setPartyObservedOptions,
      setBaseObservationData,
      (value: ReportQuestion[] | null) => setQuestionData(value || []),
      setNotes,
      setSignatures,
      setMemoryState,
      setSettings,
      ANSWER,
      setWildcard,
      setWildcardId,
      setTitle,
      setTitleManuallyEdited,
    ).catch((error) => {
      console.error('Error fetching data:', error)
    })
  }, [appID, observationID])

  React.useEffect(() => {
    if (memoryState) {
      // Use debounced version to prevent multiple rapid activation calls
      void debouncedAnswerTitleSet(memoryState)
    }
  }, [memoryState, debouncedAnswerTitleSet])

  // Cleanup debounced function on unmount
  React.useEffect(() => {
    return () => {
      debouncedAnswerTitleSet.cancel()
    }
  }, [debouncedAnswerTitleSet])

  const memoizedStripHtml = useCallback((htmlString: string) => {
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = htmlString
    return tempDiv.textContent || tempDiv.innerText || ''
  }, [])

  const handleCategoryClick = async (categoryId: number) => {
    try {
      const initialResponse = await Api.get<CategoryQuestionsResponse>(
        'questions/category_questions_list',
        {
          category: categoryId,
          application: Number(appID),
          app_questions: true,
          deleted: false,
          is_active: true,
          page: 1,
          page_size: 20,
        },
      )

      const totalQuestions = initialResponse.data.count
      const totalPages = Math.ceil(totalQuestions / 20)

      const promises = Array.from({ length: totalPages }, (_, i) =>
        Api.get<CategoryQuestionsResponse>(
          'questions/category_questions_list',
          {
            category: categoryId,
            application: Number(appID),
            app_questions: true,
            deleted: false,
            is_active: true,
            page: i + 1,
            page_size: 20,
          },
        ),
      )

      const responses = await Promise.all(promises)

      // Combine all questions from all pages
      const allQuestions = responses.reduce((acc, response) => {
        return acc.concat(response.data.results || [])
      }, [] as ReportQuestion[])

      const formattedQuestions: ReportQuestion[] = allQuestions.map(
        (q: ReportQuestion) => ({
          ...q,
          searchText: memoizedStripHtml(q.name),
        }),
      )

      // Initialize answers in memoryState for each question
      setMemoryState((prevState) => {
        if (!prevState) return prevState
        const newState = { ...prevState }

        formattedQuestions.forEach((question: ReportQuestion) => {
          const questionKey = `${question.id}-${categoryId}`
          if (!newState[questionKey]) {
            const now = new Date().toISOString()
            // Create a properly typed answer object
            const newAnswer: ReportAnswer = {
              ...ANSWER,
              question: question.id,
              category: categoryId,
              copy_sequence_number: 0,
              client_object_key: `${question.id}-${now}`,
              date_created: now,
              date_updated: now,
              observation: Number(observationID),
              // Add other required fields from ReportAnswer type
            }
            newState[questionKey] = [newAnswer]
          }
        })

        return newState
      })

      setQuestionDataByCategory((prev) => {
        const newState = {
          ...prev,
          [categoryId]: formattedQuestions,
        }
        return newState
      })
    } catch (error) {
      console.error('Error fetching questions:', error)
    }
  }

  const handleBackClick = () => {
    if (!baseObservationData) return
    void sendForm(
      memoryState,
      Api,
      signatures,
      notes,
      Number(observationID),
      stateService,
      setDisableButton,
      wildcard,
      Number(wildcardId),
      baseObservationData,
      false,
      titleManuallyEdited,
      Notification,
      Number(userId),
    )
    setSaveButtonPressed(saveButtonPressed + 1)
  }

  const handleSaveClick = () => {
    if (!baseObservationData) return
    if (settings?.reportSettings.wildcard_required && wildcard.length === 0) {
      const el = document.getElementById('wildcard-input')
      if (el) {
        const yOffset = -100
        const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset
        window.scrollTo({ top: y, behavior: 'smooth' })
      }
      return
    }

    void sendForm(
      memoryState,
      Api,
      signatures,
      notes,
      Number(observationID),
      stateService,
      setDisableButton,
      wildcard,
      Number(wildcardId),
      baseObservationData,
      false,
      titleManuallyEdited,
      Notification,
      Number(userId),
    )
    setSaveButtonPressed(saveButtonPressed + 1)
  }

  if (!memoryState || !baseObservationData) return null

  const app: ApplicationConfig = baseObservationData?.application

  return (
    <section css={baseStyle}>
      <CommonPageHeader headerText={app.observations_display} />
      <ObservationNavBar />
      <section className="page-wrap">
        <section className="header-section">
          <div className="header-part">
            <label>Title</label>
            <TextInput
              error={false}
              valueCallback={(value: string) => {
                baseObservationData.title = value
                setTitle(value)
                setTitleManuallyEdited(!!value)
              }}
              defaultValue={title}
              placeholder={'Optional'}
              height={'34px'}
              waitTime={1}
              textLimit={250}
            />
          </div>
          <div className="header-part">
            <label>Date Performed</label>
            <div
              css={css({
                ...TextInputStyle,
                backgroundColor: '#E6E6E6',
                height: 34,
                width: 185,
              })}
            >
              {moment(
                baseObservationData.date_performed ||
                  baseObservationData.date_created,
              ).format('MM/DD/YYYY')}
            </div>
          </div>
        </section>
        <hr />
        <div className="notes-wildcard-row">
          <NotesElement
            notesVariableName={app.observation_note_header}
            observationVariableName={app.observations_display}
            notes={notes}
            observationID={Number(observationID)}
            width={settings?.reportSettings.wildcard_active ? '30%' : '45%'}
          />
          <PartyObservedField
            options={partyObservedOptions.map((option) => ({
              ...option,
              value: Number(option.value),
            }))}
            setOptions={(options) =>
              setPartyObservedOptions(
                options.map((opt) => ({ ...opt, user: null })),
              )
            }
            setGlobalPO={setGlobalPO}
            partyObservedVariableFiled={app.observation_party_observed}
            width={settings?.reportSettings.wildcard_active ? '30%' : '45%'}
          />
          {settings?.reportSettings.wildcard_active && (
            <Wildcard
              wildcardHeader={settings.reportSettings.wildcard_display}
              wildcard={wildcard}
              setWildcard={setWildcard}
              required={settings.reportSettings.wildcard_required}
              width={'30%'}
            />
          )}
        </div>
        <hr />
        {baseObservationData?.categories.map((category, idx) => (
          <Category
            key={category.id}
            category={category}
            questions={questionDataByCategory[category.id] || []}
            globalPartyObserved={globalPartyObserved}
            partyObservedOptions={partyObservedOptions}
            setPartyObservedOptions={setPartyObservedOptions}
            memoryState={memoryState}
            setMemoryState={setMemoryState}
            ANSWER={ANSWER}
            app={app}
            saveButtonPressed={saveButtonPressed}
            index={idx}
            answerTitleSet={() => void debouncedAnswerTitleSet(memoryState)}
            onClick={async () => {
              await handleCategoryClick(category.id)
            }}
          />
        ))}
        <SignatureElement
          signatures={signatures}
          setSignatures={setSignatures}
        />
        <div className="bottom-buttons">
          <ButtonElement text={'Back'} functionToTrigger={handleBackClick} />
          <ButtonElement
            text={'Save'}
            functionToTrigger={handleSaveClick}
            disabled={disableButton}
            debounceTimeout={500}
            buttonType={'submit'}
          />
        </div>
      </section>
    </section>
  )
}

const baseStyle = css({
  paddingBottom: '100px',
  '.notes-wildcard-row': {
    display: 'flex',
    justifyContent: 'space-between',
    padding: 10,
  },
  '.page-wrap': {
    margin: '0px 10px 60px 0px',
    backgroundColor: 'white',
    padding: '15px 20px 20px 20px',
    '.header-section': {
      padding: 10,
      display: 'flex',
      justifyContent: 'space-between',
      '.header-part': {
        width: '45%',
      },
    },
  },
  '.bottom-buttons': {
    a: {
      width: '6%',
    },
    marginTop: 20,
    display: 'flex',
    justifyContent: 'space-between',
  },
})
